/**
 * The first page: the events, newest first, each with its dates and layouts. Admins make, rename,
 * date, delete and restore them. What the device saved shows first, then the server's answer.
 */

import { cleanDay, type EventRow } from '@mawkeb/shared/api';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { api, ApiError } from '../../api';
import { navigate } from '../../router';
import { cacheGet, cacheSet } from '../../state/cache';
import { useSession } from '../../state/session';
import { venue } from '../../state/store';
import { Dialog, Menu } from '../Dialog';
import { Dots, Plus } from '../icons';
import { loadEngine } from '../Stage';
import { ago, days } from '../time';
import { Link, PageHeader, SavedOnDevice } from './Shell';

interface Cached {
  events: EventRow[];
  at: number;
}

export function EventDialog({ event, onClose, onDone }: { event: EventRow | null; onClose: () => void; onDone: (e: EventRow) => void }) {
  const [name, setName] = useState(event?.name ?? '');
  const [startsOn, setStartsOn] = useState(event?.startsOn ?? '');
  const [endsOn, setEndsOn] = useState(event?.endsOn ?? '');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if ((startsOn && !cleanDay(startsOn)) || (endsOn && !cleanDay(endsOn))) return setProblem('Dates must be real days.');
    setBusy(true);
    setProblem(null);
    try {
      const body = { name, startsOn: startsOn || null, endsOn: endsOn || startsOn || null };
      const r = event ? await api.changeEvent(event.id, body) : await api.makeEvent(body);
      onDone(r.event);
    } catch (err) {
      setProblem((err as ApiError).status === 'offline' ? 'There’s no connection. Try again when you’re back online.' : (err as Error).message);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={event ? 'Rename or re-date the event' : 'New event'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" form="event-form" className="primary" disabled={busy || !name.trim()}>
            {event ? 'Save' : 'Make the event'}
          </button>
        </>
      }
    >
      <form id="event-form" className="stack" onSubmit={submit}>
        <label className="input">
          <span>Name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} placeholder="Sayyida Fatima 2026" required autoFocus />
        </label>
        <div className="two">
          <label className="input">
            <span>First day</span>
            <input type="date" value={startsOn} onChange={(e) => setStartsOn(e.target.value)} />
          </label>
          <label className="input">
            <span>Last day</span>
            <input type="date" value={endsOn} min={startsOn || undefined} onChange={(e) => setEndsOn(e.target.value)} />
          </label>
        </div>
        <p className="form-note">Every event is in Zahraa Hall for now.</p>
        {problem && (
          <p className="form-problem" role="alert">
            {problem}
          </p>
        )}
      </form>
    </Dialog>
  );
}

export function EventsPage() {
  const me = useSession((s) => s.me);
  const admin = me?.role === 'admin';
  const [events, setEvents] = useState<EventRow[] | null>(null);
  const [deleted, setDeleted] = useState<EventRow[]>([]);
  const [fromDevice, setFromDevice] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [editing, setEditing] = useState<EventRow | 'new' | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api.events();
      setEvents(r.events);
      setFromDevice(null);
      setProblem(null);
      void cacheSet('events', { events: r.events, at: Date.now() } satisfies Cached);
      if (admin) setDeleted((await api.events(true)).events);
    } catch (e) {
      const err = e as ApiError;
      if (err.status === 401) return;
      const saved = await cacheGet<Cached>('events');
      if (saved) {
        setEvents((now) => now ?? saved.events);
        setFromDevice(saved.at);
      } else {
        setProblem(err.status === 'offline' ? 'There’s no connection, and this device hasn’t saved the events yet.' : err.message);
      }
    }
  }, [admin]);

  useEffect(() => {
    void cacheGet<Cached>('events').then((saved) => saved && setEvents((now) => now ?? saved.events));
    void load();
    // opening a layout is quicker if the 3D engine is already here
    const idle = 'requestIdleCallback' in window ? requestIdleCallback(() => void loadEngine()) : setTimeout(() => void loadEngine(), 2000);
    return () => ('cancelIdleCallback' in window ? cancelIdleCallback(idle as number) : clearTimeout(idle as ReturnType<typeof setTimeout>));
  }, [load]);

  const remove = async (e: EventRow) => {
    if (!confirm(`Delete “${e.name}” and hide its layouts? An admin can restore it.`)) return;
    try {
      await api.deleteEvent(e.id);
      venue().toast(`Deleted ${e.name}.`, { action: { label: 'Undo', run: () => void api.restoreEvent(e.id).then(load) } });
      void load();
    } catch (err) {
      venue().toast((err as Error).message, { tone: 'bad' });
    }
  };

  const restore = async (e: EventRow) => {
    try {
      await api.restoreEvent(e.id);
      venue().toast(`Restored ${e.name}.`);
      void load();
    } catch (err) {
      venue().toast((err as Error).message, { tone: 'bad' });
    }
  };

  return (
    <div className="page">
      <PageHeader />
      <main className="page-main" id="main">
        <div className="page-title">
          <div>
            <p className="kicker">Zahraa Hall, Taha Foundation</p>
            <h1>Events</h1>
          </div>
          {admin && (
            <button type="button" className="primary with-icon" onClick={() => setEditing('new')}>
              <Plus /> New event
            </button>
          )}
        </div>
        <SavedOnDevice at={fromDevice} />
        {problem && <p className="form-problem">{problem}</p>}
        {events === null && !problem && <p className="quiet-text">Loading the events…</p>}
        {events?.length === 0 && (
          <div className="empty">
            <h2>No events yet</h2>
            <p>{admin ? 'Make the first one, then plan its layouts in the hall.' : 'An admin makes the events. Once there is one, its layouts are here.'}</p>
            {admin && (
              <button type="button" className="primary" onClick={() => setEditing('new')}>
                New event
              </button>
            )}
          </div>
        )}
        {!!events?.length && (
          <ol className="event-list">
            {events.map((e) => (
              <li key={e.id} className="event-row">
                <Link to={`/e/${e.id}`} className="event-link">
                  <span className="event-dates">{days(e.startsOn, e.endsOn) || 'No dates yet'}</span>
                  <span className="event-name" dir="auto">
                    {e.name}
                  </span>
                  <span className="event-meta">{e.layouts === 1 ? '1 layout' : `${e.layouts} layouts`}</span>
                  <span className="event-meta">Changed {ago(e.changedAt)}</span>
                </Link>
                {admin && (
                  <Menu
                    label={`${e.name}: more`}
                    icon={<Dots />}
                    items={[
                      { label: 'Rename or re-date', run: () => setEditing(e) },
                      { label: 'Delete', run: () => void remove(e), danger: true },
                    ]}
                  />
                )}
              </li>
            ))}
          </ol>
        )}
        {admin && deleted.length > 0 && (
          <details className="deleted">
            <summary>Recently deleted ({deleted.length})</summary>
            <ul>
              {deleted.map((e) => (
                <li key={e.id}>
                  <span dir="auto">{e.name}</span>
                  <button type="button" className="secondary small" onClick={() => void restore(e)}>
                    Restore
                  </button>
                </li>
              ))}
            </ul>
          </details>
        )}
      </main>
      {editing && (
        <EventDialog
          event={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={(e) => {
            setEditing(null);
            if (editing === 'new') navigate(`/e/${e.id}`);
            else void load();
          }}
        />
      )}
    </div>
  );
}
